import { useMemo, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Link } from 'react-router';
import { AUDIT_ACTIONS, useAudit, type AuditFilters } from '../hooks/useAudit';

/** Content reads are the ones that matter most on this screen, so they are badged. */
const CONTENT_ACTIONS = new Set(['view_transcript', 'view_reflections', 'view_check_ins']);

export function Audit() {
  const [actorRaw, setActorRaw] = useState('');
  const [subjectRaw, setSubjectRaw] = useState('');
  const [action, setAction] = useState<string | null>(null);
  const [actor] = useDebouncedValue(actorRaw, 300);
  const [subject] = useDebouncedValue(subjectRaw, 300);

  const filters: AuditFilters = useMemo(
    () => ({ actor, subject, action }),
    [actor, subject, action],
  );
  const query = useAudit(filters);
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Every admin read, by every admin — including your own. This log is deliberately
        not filtered to you: mutual visibility is the control.
      </Text>

      <Group align="end">
        <TextInput
          label="Actor"
          placeholder="Filter by admin email"
          value={actorRaw}
          onChange={(e) => setActorRaw(e.currentTarget.value)}
          w={240}
        />
        <TextInput
          label="Subject user id"
          placeholder="uuid"
          value={subjectRaw}
          onChange={(e) => setSubjectRaw(e.currentTarget.value)}
          w={300}
        />
        <Select
          label="Action"
          placeholder="Any"
          clearable
          data={[...AUDIT_ACTIONS]}
          value={action}
          onChange={setAction}
          w={200}
        />
      </Group>

      {query.isError && (
        <Alert color="red" title="Couldn't load the audit log">
          <Group>
            <Text size="sm">{query.error.message}</Text>
            <Button size="xs" variant="light" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </Group>
        </Alert>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>When</Table.Th>
            <Table.Th>Actor</Table.Th>
            <Table.Th>Action</Table.Th>
            <Table.Th>Subject</Table.Th>
            <Table.Th>Reason</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.id}>
              <Table.Td>{new Date(row.created_at).toLocaleString()}</Table.Td>
              <Table.Td>{row.actor_email}</Table.Td>
              <Table.Td>
                {CONTENT_ACTIONS.has(row.action) ? (
                  <Badge size="xs" color="orange" variant="light">
                    {row.action}
                  </Badge>
                ) : (
                  <Text size="sm">{row.action}</Text>
                )}
              </Table.Td>
              <Table.Td>
                {row.subject_user_id ? (
                  <Anchor component={Link} to={`/users/${row.subject_user_id}`} size="sm">
                    {row.subject_email ?? row.subject_user_id}
                  </Anchor>
                ) : (
                  '—'
                )}
              </Table.Td>
              {/* An empty cell reads as "no reason was required"; an em dash reads as
                  "none given". They are different facts, so render the dash. */}
              <Table.Td>{row.reason ?? '—'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {query.isFetching && <Loader size="sm" />}
      {!query.isFetching && rows.length === 0 && !query.isError && (
        <Text c="dimmed" size="sm">
          No audit entries match those filters.
        </Text>
      )}
      {query.hasNextPage && (
        <Button variant="light" onClick={() => void query.fetchNextPage()}>
          Load more
        </Button>
      )}
    </Stack>
  );
}
